import * as React from 'react'
import { Trans } from 'react-i18next'

import i18next from 'i18next'
import { Button, Alert, Spinner, Row, Col } from 'react-bootstrap'
import isEmail from 'validator/lib/isEmail'

import FileDropzone from 'src/components/system_wide/file_dropzone'
import StyledSelect from '../../system_wide/styled_select'
import { format, isValid, parse } from 'date-fns'

// Cambio nome da Worker a createWorker per evitare conflitti con la classe nativa Worker
const createWorker = () => new Worker(new URL('./dealers-parser.ww.js', import.meta.url), { type: 'module' })

const { t } = i18next

const initialState = {
  dimmed: false,
  worker: undefined,
  filename: '',
  showDragzone: true,
  parseOk: true,
  headerOptions: [],
  errors: [],
  result: [],
  loading: false,
  columnMapping: {}, // aggiunto per mappare colonne
  summaryMessage: '',
  validData: [],
}

const possibleDateFormats = ['dd-MM-yyyy', 'dd/MM/yyyy', 'd-M-yy', 'd/M/yy']

const parseDateFlexible = input => {
  if (!input || typeof input !== 'string') return null
  for (const formatStr of possibleDateFormats) {
    const parsed = parse(input, formatStr, new Date())
    if (isValid(parsed)) {
      return format(parsed, 'yyyy-MM-dd')
    }
  }
  return null
}

class XLSXParser extends React.Component {
  static defaultProps = {
    dimmed: false,
    onDataReady: () => {},
    ssn_validators: [],
    operationType: 'import',
  }

  xlsxTrack = [
    {
      label: t('components.dealers.piva', 'Partita IVA'),
      value: 'piva',
      required: true,
      transform: val => {
        const str = val?.toString().trim() || ''
        return str.length === 11 ? str : null
      },
      validate: val => !val || /^\d{11}$/.test(val) || 'Deve essere una stringa di 11 cifre',
    },
    {
      label: t('components.dealers.business_name', 'Nome Azienda'),
      value: 'business_name',
      required: this.props.operationType === 'import',
      transform: val => val?.toString().trim() || null,
      validate: val => !val || val.length > 0 || 'Nome Azienda non valido',
    },
    {
      label: t('components.dealer_brands_form.affiliation_date', 'Data Affiliazione'),
      value: 'affiliation_date',
      transform: val => parseDateFlexible(val),
      validate: val => {
        const parsed = parseDateFlexible(val)
        return parsed ? true : 'Data non valida o formato sconosciuto'
      },
    },
    {
      label: t('components.dealers.commercial_email', 'Email Commerciale'),
      value: 'commercial_email',
      transform: val => val?.toString().trim().toLowerCase() || null,
      validate: val => !val || isEmail(val) || 'Email Commerciale non valida',
    },
    {
      label: t('components.dealers.administrative_email', 'Email Amministrativa'),
      value: 'administrative_email',
      transform: val => val?.toString().trim().toLowerCase() || null,
      validate: val => !val || isEmail(val) || 'Email Amministrativa non valida',
    },
    {
      label: t('components.dealers.phone', 'Telefono'),
      value: 'phone',
      transform: val => val?.toString().trim() || null,
      validate: val => true, // nessuna validazione
    },
    {
      label: t('components.dealers.lat', 'Indirizzo'),
      value: 'address',
      transform: val => val?.toString().trim() || null,
      validate: val => true, // nessuna validazione
    },
    {
      label: t('components.dealers.cap', 'CAP'),
      value: 'cap',
      transform: val => val?.toString().trim() || null,
      validate: val => true, // eventualmente: /^\d{5}$/.test(val)
    },
    {
      label: t('components.dealers.province', 'Provincia'),
      value: 'province',
      transform: val => val?.toString().trim() || null,
      validate: val => true,
    },
    {
      label: t('components.dealers.region', 'Regione'),
      value: 'region',
      transform: val => val?.toString().trim() || null,
      validate: val => true,
    },
    {
      label: t('components.dealers.city', 'Città'),
      value: 'city',
      transform: val => val?.toString().trim() || null,
      validate: val => true,
    },
    {
      label: t('components.dealer_brands_form.sig_fullname', 'Firmatario'),
      value: 'sig_fullname',
      transform: val => val?.toString().trim() || null,
      validate: val => true,
    },
    {
      label: t('components.dealer_brands_form.sig_ssn', 'Codice Fiscale Firmatario'),
      value: 'sig_ssn',
      transform: val => val?.toString().trim() || null,
      validate: val => true,
    },
    {
      label: t('components.dealer_brands_form.code', 'Codice'),
      value: 'code',
      transform: val => val?.toString().trim() || null,
      validate: val => true,
    },
    {
      label: t('components.dealer.agent', 'Funzionario'),
      value: 'agent',
      transform: val => val?.toString().trim() || null,
      validate: val => true,
    },
  ]

  state = { ...initialState }

  componentWillUnmount() {
    if (this.state.worker) {
      this.state.worker.terminate()
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.operationType && prevProps.operationType !== this.props.operationType) {
      this.xlsxTrack = this.xlsxTrack.map(track => {
        if (track.value === 'business_name') {
          return {
            ...track,
            required: this.props.operationType === 'import',
          }
        }
        return track
      })
      this.forceUpdate()
    }
  }

  onDrop = file => {
    if (/xlsx$/.test(file.name)) {
      this.setState({ dimmed: true, filename: file.name })
      this.handleFileAsync(file)
    } else {
      this.setState({
        dimmed: false,
        showDragzone: false,
        parseOk: false,
        errors: [t('system_wide.bulk_import.invalid_format_file', 'Invalid file format')],
      })
    }
  }

  onParse = async data => {
    let { state, header, rows } = data

    if (state !== 'SUCCESS') {
      this.setState({
        dimmed: false,
        showDragzone: false,
        parseOk: false,
        loading: false,
        errors: [t('system_wide.bulk_import.invalid_track', 'Invalid track')],
      })
      return
    }

    if (rows.length === 0) {
      this.setState({
        dimmed: false,
        showDragzone: false,
        parseOk: false,
        loading: false,
        errors: [t('system_wide.bulk_import.empty_file', 'Empty file')],
      })
      return
    }
    let headerOptions = Object.keys(header)
      .filter(k => header[k] !== '')
      .map(k => ({ label: header[k], value: k }))

    this.setState({
      dimmed: false,
      showDragzone: false,
      parseOk: true,
      headerOptions,
      result: rows,
      loading: false,
      columnMapping: {}, // reset mapping ad ogni nuovo file
    })
  }

  handleFileAsync = file => {
    let worker = createWorker()
    this.setState({ worker, loading: true, showDragzone: false })

    worker.onmessage = event => {
      this.onParse(event.data)
      this.setState({ worker: undefined })
      worker.terminate()
    }
    worker.onerror = event => {
      console.error('Worker error', event)
      this.setState({
        dimmed: false,
        showDragzone: false,
        parseOk: false,
        errors: [t('system_wide.bulk_import.worker_error', 'Parsing error')],
        worker: undefined,
      })
      worker.terminate()
    }

    worker.postMessage(file)
  }

  handleSelectChange = (fieldName, selectedOption) => {
    this.setState(prevState => {
      let newMapping = { ...prevState.columnMapping }

      // Imposta la nuova associazione
      if (selectedOption) {
        newMapping[fieldName] = selectedOption.value
      } else {
        // Se viene rimosso (clear), elimina la chiave
        delete newMapping[fieldName]
      }

      return { columnMapping: newMapping }
    })
  }

  validateData = data => {
    const errors = []
    const validData = []
    const seenPiva = new Set()

    data.forEach((row, rowIndex) => {
      let rowErrors = []
      const transformedRow = {}

      this.xlsxTrack.forEach(field => {
        const rawVal = row[field.value]
        if (!(field.value in row)) {
          return
        }
        // Controllo required
        if (field.required && (rawVal === null || rawVal === undefined || rawVal === '')) {
          rowErrors.push({
            row: rowIndex + 2,
            field: field.value,
            message: `${field.label} è obbligatorio ma non valorizzato`,
          })
          return
        }

        // Salta se vuoto e non required
        if (rawVal === null || rawVal === undefined || rawVal === '') {
          transformedRow[field.value] = null // o '' a scelta
          return
        }

        // Validazione custom se presente
        if (field.validate) {
          const result = field.validate(rawVal)
          if (result !== true) {
            rowErrors.push({
              row: rowIndex + 2,
              field: field.value,
              message: typeof result === 'string' ? result : `${field.label} non è valido: "${rawVal}"`,
            })
            return
          }
        }

        // Se siamo qui, il valore è valido => lo trasformo e salvo
        transformedRow[field.value] = field.transform ? field.transform(rawVal) : rawVal
      })

      if (rowErrors.length > 0) {
        errors.push(...rowErrors)
        return
      }

      // Ora che la riga è considerata valida, controllo duplicato piva
      const pivaVal = transformedRow['piva']
      if (pivaVal) {
        if (seenPiva.has(pivaVal)) {
          errors.push({
            row: rowIndex + 2,
            field: 'piva',
            message: `La partita IVA "${pivaVal}" è già stata inserita precedentemente e viene scartata.`,
          })
          return
        } else {
          seenPiva.add(pivaVal)
        }
      }

      validData.push(transformedRow)
    })

    return { errors, validData }
  }

  processData = () => {
    let { result, columnMapping } = this.state

    let data = result
      .map(row => {
        let mappedRow = {}
        for (let [fieldName, columnName] of Object.entries(columnMapping)) {
          mappedRow[fieldName] = row[columnName]
        }
        return mappedRow
      })
      .filter(row => {
        return Object.values(row).some(value => String(value ?? '').trim() !== '')
      })

    // validazione automatica
    const { errors, validData } = this.validateData(data)
    const summaryMessage = `${validData.length} righe valide pronte per l'importazione, ${
      data.length - validData.length
    } righe scartate per errori.`
    if (errors.length > 0) {
      this.setState({
        errors: errors.map(err => `Riga ${err.row} - Campo "${err.field}": ${err.message}`),
        parseOk: false,
        summaryMessage,
        validData,
      })
    } else {
      this.setState({
        errors: [],
        parseOk: true,
        summaryMessage: `${validData.length} righe valide pronte per l'importazione.`,
        validData,
      })
    }
  }

  resetData = () => {
    this.setState({
      dimmed: false,
      showDragzone: true,
      parseOk: true,
      errors: [],
      headerOptions: [],
      result: [],
      loading: false,
      columnMapping: {},
      summaryMessage: '',
      validData: [],
    })
  }

  render() {
    let { dimmed, showDragzone, parseOk, errors, headerOptions, result, loading, columnMapping, summaryMessage } =
      this.state
    if (loading) {
      return (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      )
    }
    let can_proceed = this.xlsxTrack.filter(field => field.required).every(field => !!columnMapping[field.value])
    return (
      <div className={`bulk-import ${dimmed ? 'dimmed' : ''}`}>
        {showDragzone && (
          <FileDropzone onDrop={this.onDrop} className="dropzone">
            <p>
              <Trans i18nKey="system_wide.bulk_import.drop_file">
                Trascina qui il file XLSX oppure clicca per selezionarlo
              </Trans>
            </p>
          </FileDropzone>
        )}
        {summaryMessage && (
          <Alert variant="info" className="mt-3 d-flex flex-column align-items-start">
            <div>{this.state.summaryMessage}</div>
            <div className="d-flex flex-row mt-1">
              <Button variant="secondary" onClick={this.resetData}>
                <Trans i18nKey="system_wide.bulk_import.next_step">Reset</Trans>
              </Button>
              {this.state.validData.length > 0 && (
                <Button variant="primary" onClick={() => this.props.onDataReady(this.state.validData)}>
                  Conferma e importa dati validi
                </Button>
              )}
            </div>
          </Alert>
        )}
        {!parseOk && (
          <div>
            <Alert
              variant="danger"
              className="error-messages"
              style={{ maxHeight: '40vh', overflowY: 'auto', overflowX: 'hidden' }}>
              {errors.map((e, i) => (
                <div key={i}>{e}</div>
              ))}
            </Alert>
          </div>
        )}
        {!showDragzone && parseOk && (
          <>
            <div className="d-flex flex-row justify-content-between">
              <div>
                <div>
                  <b>
                    <Trans i18nKey="system_wide.bulk_import.columns_found">Colonne trovate</Trans>:
                  </b>{' '}
                  {headerOptions.length}
                </div>
                <div>
                  <b>
                    <Trans i18nKey="system_wide.bulk_import.rows_found">Righe trovate</Trans>:
                  </b>{' '}
                  {result.length}
                </div>
              </div>
              <div>
                <Button onClick={this.resetData}>
                  <Trans i18nKey="system_wide.bulk_import.next_step">Reset</Trans>
                </Button>
                <Button onClick={this.processData} disabled={!can_proceed}>
                  <Trans i18nKey="system_wide.bulk_import.next_step">Procedi</Trans>
                </Button>
              </div>
            </div>
            <div>
              <p>
                <Trans i18nKey="system_wide.bulk_import.selection_column">
                  Associa i dati del dealer alla rispettiva colonna del file
                </Trans>
              </p>
              <Row className="mb-2">
                <Col md={4}>
                  <b>
                    <Trans i18nKey="system_wide.bulk_import.column_dealer">Dealer</Trans>
                  </b>
                </Col>
                <Col md={4}>
                  <b>
                    <Trans i18nKey="system_wide.bulk_import.column_xlsx">Colonna file</Trans>
                  </b>
                </Col>
                <Col md={4}>
                  <b>
                    <Trans i18nKey="system_wide.bulk_import.example">Esempio</Trans>
                  </b>
                </Col>
              </Row>
            </div>
            <div className="container-fluid" style={{ maxHeight: '40vh', overflowY: 'auto', overflowX: 'hidden' }}>
              {this.xlsxTrack.map((field, i) => {
                const isMapped = !!columnMapping[field.value] // controlla se è stato selezionato
                return (
                  <Row
                    key={i}
                    className="align-items-center mb-2"
                    style={{
                      backgroundColor: isMapped ? '#d4edda' : '',
                      borderRadius: '4px',
                    }}>
                    <Col md={4}>
                      <b>{field.label}</b>
                      {field.required && <span style={{ color: 'red' }}> *</span>}
                    </Col>
                    <Col md={4}>
                      <StyledSelect
                        name={field.value}
                        isClearable
                        placeholder={t('system_wide.bulk_import.column', 'Colonna')}
                        value={headerOptions.find(opt => opt.value === columnMapping[field.value]) || null}
                        onChange={option => this.handleSelectChange(field.value, option)}
                        options={headerOptions}
                      />
                    </Col>
                    <Col md={4} style={{ fontStyle: 'italic' }}>
                      {columnMapping[field.value] && result[0] && result[0][columnMapping[field.value]] !== undefined
                        ? result[0][columnMapping[field.value]]
                        : '-'}
                    </Col>
                  </Row>
                )
              })}
            </div>
          </>
        )}
      </div>
    )
  }
}

export default XLSXParser
